import { STUDY_SUBJECTS as DEFAULT_SUBJECTS, studySubjects } from '../../dailyStudySubjects';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { turkeyDate, shiftDate, studyWindow, studyComparison, studyComparisonText } from '../student360Rules';
import { fetchJson } from '../../apiBase';
import { Card } from '../components/ui';
import { Chip, MiniArea, useChartWidth } from '../components/charts';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import trLocale from 'date-fns/locale/tr';



function StatPill({ label, value, strong, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
      <span style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', fontSize: 13, color: color || (strong ? '#0F1E56' : '#334155'), background: '#f8fafc', fontWeight: strong ? 700 : 500 }}>
        {value ?? '-'}
      </span>
    </div>
  );
}

function toPickerDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toIsoDate(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function StudySummary({
  study,
  studentId,
  onStudyChanged,
  canWriteStudy = true,
  canWriteBooks = true,
  splitLayout = false,
  studyMount = null,
  booksMount = null,
  today = turkeyDate(),
  onEntriesLoaded,
  onPendingBooksLoaded,
  requestOpenStudyLogModalToken = 0,
  requestOpenBookModalToken = 0,
}) {
  const dialogRef = useRef(null);
  const modalBusy = useRef(false);
  const alive = useRef(true);
  const [studyError, setStudyError] = useState(false);
  const [pendingError, setPendingError] = useState(false);
  const [studyLimit, setStudyLimit] = useState(5);
  const [bookLimit, setBookLimit] = useState(5);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const width = useChartWidth(90); // 32(body)+32(card)+24(inner)+2(border)
  const [windowSel, setWindowSel] = useState('7'); // '7' | '30'
  const [booksScope, setBooksScope] = useState('30'); // '30' | 'all'
  useEffect(() => { setBookLimit(5); }, [booksScope]);
  useEffect(() => { setStudyLimit(5); }, [windowSel]);
  const [booksBusy, setBooksBusy] = useState(false);
  const [pendingBooks, setPendingBooks] = useState([]);
  const [bookNotice, setBookNotice] = useState('');
  const [bookModalMode, setBookModalMode] = useState(null); // null | 'add' | 'edit' | 'delete'
  const [bookAddStep, setBookAddStep] = useState(1); // 1: title+search, 2: page confirmation
  const [bookSearchResult, setBookSearchResult] = useState(null);
  const [bookModalError, setBookModalError] = useState('');
  const [bookTarget, setBookTarget] = useState(null);
  const [bookForm, setBookForm] = useState({ title: '', pageCount: '' });
  const [studyEntries, setStudyEntries] = useState([]);
  const [studyBusy, setStudyBusy] = useState(false);
  const [studyModalMode, setStudyModalMode] = useState(null); // null | 'add' | 'edit' | 'delete'
  const [studyModalError, setStudyModalError] = useState('');
  const [studyTarget, setStudyTarget] = useState(null);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const STUDY_SUBJECTS = studySubjects(subjectOptions, DEFAULT_SUBJECTS);
  const ENTRY_SUBJECTS = studySubjects(subjectOptions, DEFAULT_SUBJECTS, true);
  const [studyForm, setStudyForm] = useState({ date: '', subjects: {}, pages: '' });
  const lastOpenRequestRef = useRef(0);
  const lastOpenBookRequestRef = useRef(0);
  const isMonthly = windowSel === '30';
  const windowData = useMemo(() => studyWindow(studyEntries, today, isMonthly ? 30 : 7), [studyEntries, today, isMonthly]);
  const subjectTotals = useMemo(() => studyEntries.filter((entry) => entry.date >= shiftDate(today, isMonthly ? -29 : -6) && entry.date <= today).reduce((totals, entry) => {
    Object.entries(entry.subjects || {}).forEach(([key, value]) => { totals[key] = (totals[key] || 0) + Number(value || 0); });
    return totals;
  }, {}), [studyEntries, today, isMonthly]);
  const questionsArr = windowData.questions.filter((value) => value !== null);
  const pagesArr = windowData.pages.filter((value) => value !== null);
  const dates7 = windowData.dates.filter((_, index) => windowData.questions[index] !== null);
  const latestQ = questionsArr.length ? questionsArr[questionsArr.length - 1] : null;
  const latestPages = pagesArr.length ? pagesArr[pagesArr.length - 1] : null;
  const fallbackBooks = Array.isArray(study?.completed_books) ? study.completed_books : [];
  const completedBooksAll = Array.isArray(study?.completed_books_all)
    ? study.completed_books_all
    : fallbackBooks;
  const completedBooksLast30 = Array.isArray(study?.completed_books_last30)
    ? study.completed_books_last30
    : completedBooksAll.filter((book) => book.date >= shiftDate(today, -29) && book.date <= today);
  const completedBooks = booksScope === 'all' ? completedBooksAll : completedBooksLast30;
  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul', day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return iso;
    }
  };
  const formatPageCount = (book) => {
    const raw = book?.page_count ?? book?.pageCount ?? book?.pages;
    const val = Number(raw);
    if (!Number.isFinite(val) || val < 0) return '-';
    return String(Math.round(val));
  };
  const parseApiError = async (res, fallback) => {
    try {
      const js = await res.json();
      const detail = js && js.detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
    } catch (_) {
      // no-op
    }
    return fallback;
  };
  const refreshStudyEntries = useCallback(async () => {
    if (!studentId) return;
    setStudyBusy(true);
    try {
      const res = await fetchJson(`/parent/study-logs?student_id=${encodeURIComponent(String(studentId))}&days=60`);
      if (!res.ok) throw new Error('Çalışma kayıtları yüklenemedi.');
      const js = await res.json();
      if (!alive.current) return;
      const entries = Array.isArray(js?.items) ? js.items : [];
      setSubjectOptions(js?.subject_options || []);
      setStudyEntries(entries); setStudyError(false); onEntriesLoaded?.(entries);
    } catch (_) {
      if (alive.current) { setStudyError(true); onEntriesLoaded?.(null); }
    } finally { if (alive.current) setStudyBusy(false); }
  }, [studentId, onEntriesLoaded]);
  useEffect(() => { refreshStudyEntries(); }, [refreshStudyEntries]);

  const refreshPendingBooks = useCallback(async () => {
    if (!studentId) return;
    const res = await fetchJson(`/parent/book-claims?student_id=${encodeURIComponent(String(studentId))}`);
    if (!res.ok) throw new Error('Onay bekleyen kitaplar yüklenemedi.');
    const payload = await res.json();
    return payload.items || [];
  }, [studentId]);
  useEffect(() => {
    let active = true;
    setPendingBooks([]);
    setBookNotice('');
    // This list is a point-in-time view. Teacher approval/rejection is rare
    // while a parent keeps the page open; do not poll every 15 seconds.
    // User-initiated book submission refreshes this list immediately below.
    const loadOnce = async () => {
      try {
        const items = await refreshPendingBooks();
        if (!active || !items) return;
        setPendingBooks(items); setPendingError(false); onPendingBooksLoaded?.(items);
      } catch (_) { if (active) { setPendingError(true); onPendingBooksLoaded?.(null); } }
    };
    loadOnce();
    return () => { active = false; };
  }, [refreshPendingBooks, onPendingBooksLoaded]);

  const openAddStudyModal = useCallback(() => {
    if (!canWriteStudy) return;
    setStudyModalError('');
    setStudyTarget(null);
    setStudyForm({
      date: today,
      subjects: {},
      pages: '',
    });
    setStudyModalMode('add');
  }, [canWriteStudy, today]);
  const openEditStudyModal = (entry) => {
    setStudyModalError('');
    setStudyTarget(entry || null);
    setStudyForm({
      date: String(entry?.date || ''),
      subjects: Object.fromEntries(Object.entries(entry?.subjects || {}).map(([key, value]) => [key, String(value ?? '')])),
      pages: String(entry?.pages ?? ''),
    });
    setStudyModalMode('edit');
  };
  const openDeleteStudyModal = (entry) => {
    setStudyModalError('');
    setStudyTarget(entry || null);
    setStudyModalMode('delete');
  };
  const closeStudyModal = (force = false) => {
    if (studyBusy && force !== true) return;
    setStudyModalMode(null);
    setStudyModalError('');
    setStudyTarget(null);
  };
  const submitStudyModal = async () => {
    if (!canWriteStudy || !studentId || studyBusy || !studyModalMode) return;
    if (studyModalMode === 'delete') {
      const targetDate = String(studyTarget?.date || '');
      if (!targetDate) {
        setStudyModalError('Silinecek kayıt tarihi bulunamadı.');
        return;
      }
      setStudyBusy(true);
      try {
        const res = await fetchJson(`/parent/study-logs?student_id=${encodeURIComponent(String(studentId))}&date=${encodeURIComponent(targetDate)}`, {
          method: 'DELETE',
        });
        if (!res.ok) {
          setStudyModalError(await parseApiError(res, 'Çalışma kaydı silinemedi.'));
          return;
        }
        closeStudyModal(true);
        await refreshStudyEntries();
        if (alive.current && typeof onStudyChanged === 'function') await onStudyChanged();
      } catch (_) {
        if (alive.current) setStudyModalError('İşlem tamamlanamadı. Yeniden deneyin.');
      } finally {
        setStudyBusy(false);
      }
      return;
    }

    const dateVal = String(studyForm.date || '').trim();
    const pagesVal = Number(String(studyForm.pages || '0').trim());
    const rawSubjects = Object.fromEntries(Object.entries(studyForm.subjects || {})
      .filter(([key]) => ENTRY_SUBJECTS.some(([subjectKey]) => subjectKey === key))
      .map(([key, value]) => [key, Number(String(value || '0').trim())]));
    if (!dateVal) {
      setStudyModalError('Tarih zorunludur.');
      return;
    }
    if (Object.values(rawSubjects).some((value) => !Number.isFinite(value) || value < 0 || value > 10000) || !Number.isFinite(pagesVal) || pagesVal < 0) {
      setStudyModalError('Ders soru sayıları ve sayfa için geçerli değer giriniz.');
      return;
    }
    const subjects = Object.fromEntries(Object.entries(rawSubjects).filter(([, value]) => value > 0));

    setStudyBusy(true);
    try {
      const res = await fetchJson('/parent/study-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: String(studentId),
          date: dateVal,
          subjects: Object.fromEntries(Object.entries(subjects).map(([key, value]) => [key, Math.round(value)])),
          pages: Math.round(pagesVal),
        }),
      });
      if (!res.ok) {
        setStudyModalError(await parseApiError(res, 'Çalışma kaydı kaydedilemedi.'));
        return;
      }
      closeStudyModal(true);
      await refreshStudyEntries();
      if (alive.current && typeof onStudyChanged === 'function') await onStudyChanged();
    } catch (_) {
      if (alive.current) setStudyModalError('İşlem tamamlanamadı. Yeniden deneyin.');
    } finally {
      setStudyBusy(false);
    }
  };

  useEffect(() => {
    const token = Number(requestOpenStudyLogModalToken || 0);
    if (!Number.isFinite(token) || token <= 0) return;
    if (token === lastOpenRequestRef.current) return;
    lastOpenRequestRef.current = token;
    openAddStudyModal();
  }, [openAddStudyModal, requestOpenStudyLogModalToken]);

  const resetAddFlow = () => {
    setBookAddStep(1);
    setBookSearchResult(null);
    setBookForm({ title: '', pageCount: '' });
  };
  const openAddModal = () => {
    setBookModalError('');
    setBookTarget(null);
    resetAddFlow();
    setBookModalMode('add');
  };

  useEffect(() => {
    const token = Number(requestOpenBookModalToken || 0);
    if (!canWriteBooks || !Number.isFinite(token) || token <= 0) return;
    if (token === lastOpenBookRequestRef.current) return;
    lastOpenBookRequestRef.current = token;
    setBookModalError('');
    setBookTarget(null);
    setBookAddStep(1);
    setBookSearchResult(null);
    setBookForm({ title: '', pageCount: '' });
    setBookModalMode('add');
  }, [requestOpenBookModalToken, canWriteBooks]);

  const openEditModal = (book) => {
    setBookModalError('');
    setBookTarget(book || null);
    setBookForm({
      title: String(book?.title || ''),
      pageCount: formatPageCount(book) === '-' ? '' : formatPageCount(book),
    });
    setBookModalMode('edit');
  };
  const openDeleteModal = (book) => {
    setBookModalError('');
    setBookTarget(book || null);
    setBookModalMode('delete');
  };
  const closeBookModal = (force = false) => {
    if (booksBusy && force !== true) return;
    setBookModalMode(null);
    setBookModalError('');
    setBookTarget(null);
    resetAddFlow();
  };
  const searchForAddBook = async () => {
    if (!studentId || booksBusy) return;
    const title = String(bookForm.title || '').trim();
    if (!title) {
      setBookModalError('Kitap adı zorunludur.');
      return;
    }

    setBookModalError('');
    setBooksBusy(true);
    try {
      const res = await fetchJson(`/parent/books/search?student_id=${encodeURIComponent(String(studentId))}&q=${encodeURIComponent(title)}`);
      if (!res.ok) {
        setBookModalError(await parseApiError(res, 'Kitap araması yapılamadı.'));
        return;
      }
      const payload = await res.json();
      const exact = payload?.exact_match || null;
      const similar = Array.isArray(payload?.similar_matches) ? payload.similar_matches : [];
      setBookSearchResult({ exactMatch: exact, similarMatches: similar });

      if (exact) {
        setBookForm((prev) => ({
          ...prev,
          title: String(exact?.title || prev.title || ''),
          pageCount: exact?.page_count != null ? String(exact.page_count) : '',
        }));
        setBookAddStep(2);
        return;
      }
      if (!similar.length) {
        setBookAddStep(2);
      }
    } catch (_) {
      if (alive.current) setBookModalError('İşlem tamamlanamadı. Yeniden deneyin.');
    } finally {
      setBooksBusy(false);
    }
  };
  const selectSimilarBook = (candidate) => {
    setBookForm({
      title: String(candidate?.title || ''),
      pageCount: candidate?.page_count != null ? String(candidate.page_count) : '',
    });
    setBookModalError('');
    setBookAddStep(2);
  };
  const continueAsNewBook = () => {
    setBookForm((prev) => ({ ...prev, pageCount: '' }));
    setBookModalError('');
    setBookAddStep(2);
  };
  const submitBookModal = async () => {
    if (!canWriteBooks || !studentId || booksBusy || !bookModalMode) return;
    if (bookModalMode === 'delete') {
      const completionId = Number(bookTarget?.completion_id);
      if (!Number.isFinite(completionId) || completionId <= 0) {
        setBookModalError('Bu kayıt silinemiyor.');
        return;
      }
      setBooksBusy(true);
      try {
        const res = await fetchJson(`/parent/completed-books/${completionId}?student_id=${encodeURIComponent(String(studentId))}`, {
          method: 'DELETE',
        });
        if (!res.ok) {
          setBookModalError(await parseApiError(res, 'Kitap silinemedi.'));
          return;
        }
        closeBookModal(true);
        if (alive.current && typeof onStudyChanged === 'function') await onStudyChanged();
      } finally {
        setBooksBusy(false);
      }
      return;
    }

    const title = String(bookForm.title || '').trim();
    const pageCount = Number(String(bookForm.pageCount || '').trim());
    if (!title) {
      setBookModalError('Kitap adı zorunludur.');
      return;
    }
    if (!Number.isFinite(pageCount) || pageCount <= 0) {
      setBookModalError('Geçerli bir sayfa sayısı giriniz.');
      return;
    }

    setBooksBusy(true);
    try {
      if (bookModalMode === 'add') {
        const res = await fetchJson('/parent/completed-books', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            student_id: String(studentId),
            title,
            page_count: Math.round(pageCount),
          }),
        });
        if (!res.ok) {
          setBookModalError(await parseApiError(res, 'Kitap eklenemedi.'));
          return;
        }
        const result = await res.json();
        if (result.status === 'PENDING_TEACHER_APPROVAL') {
          setBookNotice('Kitap öğretmen onayına gönderildi.');
          const items = await refreshPendingBooks();
          if (!alive.current) return;
          setPendingBooks(items || []); onPendingBooksLoaded?.(items || []);
        } else {
          setBookNotice('Kitap kütüphanede kayıtlı.');
        }
      } else {
        const completionId = Number(bookTarget?.completion_id);
        if (!Number.isFinite(completionId) || completionId <= 0) {
          setBookModalError('Bu kayıt düzenlenemiyor.');
          return;
        }
        const res = await fetchJson(`/parent/completed-books/${completionId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            student_id: String(studentId),
            title,
            page_count: Math.round(pageCount),
            finished_date: bookTarget?.date || undefined,
          }),
        });
        if (!res.ok) {
          setBookModalError(await parseApiError(res, 'Kitap güncellenemedi.'));
          return;
        }
      }
      closeBookModal(true);
      if (alive.current && typeof onStudyChanged === 'function') await onStudyChanged();
    } catch (_) {
      if (alive.current) setBookModalError('İşlem tamamlanamadı. Yeniden deneyin.');
    } finally {
      setBooksBusy(false);
    }
  };

  modalBusy.current = studyBusy || booksBusy;
  const isModalOpen = !!studyModalMode || !!bookModalMode;
  // The form host remains mounted independently of the detail panels.
  useEffect(() => {
    if (!isModalOpen) return undefined;
    const previous = document.activeElement;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('input, button, select')?.focus();
    const keydown = (event) => {
      if (event.key === 'Escape' && !modalBusy.current) { setStudyModalMode(null); setBookModalMode(null); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]') || []);
      if (!controls.length) return;
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = oldOverflow; document.removeEventListener('keydown', keydown); if (previous?.isConnected) previous.focus(); };
  }, [isModalOpen]);

  if (!study) return null;
  const studyContent = <Card title="Çalışma Özeti">
    {studyError && <div className="student360-error">Çalışma kayıtları alınamadı. <button onClick={refreshStudyEntries}>Yeniden dene</button></div>}
      {/* Window toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button onClick={() => setWindowSel('7')} style={{ background: 'transparent', border: 'none', padding: 0 }}><Chip label="Son 7 gün" active={windowSel === '7'} /></button>
        <button onClick={() => setWindowSel('30')} style={{ background: 'transparent', border: 'none', padding: 0 }}><Chip label="Son 30 gün" active={windowSel === '30'} /></button>
      </div>

      {/* Questions chart (styled like SubjectPerformance) */}
      <div style={{ background: '#fff', border: '1px solid #eef2f7', borderLeft: '4px solid #10B981', borderRadius: 12, padding: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '2px 0 8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
            <div style={{ fontWeight: 700 }}>Günlük Soru</div>
          </div>
          <span style={{ fontSize: 12, color: "#64748b" }}>Son kayıt: {latestQ ?? "—"}</span>
        </div>
        {studyBusy || studyError ? <p role="status">{studyError ? 'Çalışma kayıtları alınamıyor.' : 'Çalışma bilgileri yükleniyor…'}</p> : <MiniArea values={questionsArr} dates={dates7} showAvg={windowData.avgQuestions != null} avg={windowData.avgQuestions} width={width} color="#10B981" />}
      </div>

      {/* Pages chart */}
      <div style={{ background: '#fff', border: '1px solid #eef2f7', borderLeft: '4px solid #8B5CF6', borderRadius: 12, padding: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '2px 0 8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#8B5CF6', display: 'inline-block' }} />
            <div style={{ fontWeight: 700 }}>Günlük Sayfa</div>
          </div>
          <span style={{ fontSize: 12, color: "#64748b" }}>Son kayıt: {latestPages ?? "—"}</span>
        </div>
        {studyBusy || studyError ? <p role="status">{studyError ? 'Çalışma kayıtları alınamıyor.' : 'Çalışma bilgileri yükleniyor…'}</p> : <MiniArea values={pagesArr} dates={dates7} showAvg={windowData.avgPages != null} avg={windowData.avgPages} width={width} color="#8B5CF6" />}
      </div>

      {/* Lesson question totals and page totals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginTop: 8 }}>
        <StatPill label="Toplam Soru" value={studyError || studyBusy ? '—' : windowData.totalQuestions} strong />
        <StatPill label="Toplam Sayfa" value={studyError || studyBusy ? '—' : windowData.totalPages} strong />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginTop: 8 }}>
        <StatPill label="Kayıtlı gün başına soru" value={windowData.avgQuestions == null || studyError ? "—" : Number(windowData.avgQuestions.toFixed(1))} />
        <StatPill label="Kayıtlı gün başına sayfa" value={windowData.avgPages == null || studyError ? "—" : Number(windowData.avgPages.toFixed(1))} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {STUDY_SUBJECTS.filter(([key]) => Number(subjectTotals[key] || 0) > 0).map(([key, label]) => (
          <StatPill key={key} label={label} value={`${subjectTotals[key]} soru`} />
        ))}
      </div>

      <p style={{ fontSize: 13, color: '#64748b' }}>Son {isMonthly ? 30 : 7} gün: {studyError || studyBusy ? 'Bilgi alınamıyor' : `${windowData.recordedDays}/${isMonthly ? 30 : 7} gün kayıt`}. Grafikler yalnız kayıtlı günleri gösterir.</p>
      <p style={{ fontSize: 12, color: '#64748b' }}>{studyComparisonText(studyComparison(studyError || studyBusy ? null : studyEntries, today, isMonthly ? 30 : 7), isMonthly ? 30 : 7)}</p>
      {/* Daily study logs CRUD */}
      <div style={{ marginTop: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, color: '#0F172A' }}>Günlük Çalışma Kayıtları</div>
          {canWriteStudy && (<button
            type="button"
            onClick={openAddStudyModal}
            disabled={studyBusy || !studentId}
            style={{
              border: '1px solid #bae6fd',
              background: '#ffffff',
              color: '#0c4a6e',
              borderRadius: 999,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 700,
              cursor: studyBusy || !studentId ? 'not-allowed' : 'pointer',
            }}
          >
            Kayıt Ekle
          </button>)}
        </div>

        {!studyEntries.length && !studyError && !studyBusy && (
          <div style={{ padding: '10px 12px', background: '#fff', borderRadius: 10, border: '1px dashed #e5e7eb', color: '#475569', fontSize: 14 }}>
            Henüz çalışma kaydı yok.
          </div>
        )}

        {!!studyEntries.length && (
          <div style={{ display: 'grid', gap: 8 }}>
            {studyEntries.slice(0, studyLimit).map((entry) => (
              <div
                key={`study-${entry.date}`}
                style={{
                  background: '#fff',
                  borderRadius: 10,
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div style={{ minWidth: 0, flex: '1 1 180px' }}>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{formatDate(entry.date)}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {STUDY_SUBJECTS.filter(([key]) => Number(entry.subjects?.[key] || 0) > 0)
                      .map(([key, label]) => `${label}: ${Number(entry.subjects[key])} soru`).join(' • ') || 'Ders soru kaydı yok'}
                    {` • ${Number(entry.pages || 0)} sayfa`}
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8 }}>
                  {canWriteStudy && (<button
                    type="button"
                    onClick={() => openEditStudyModal(entry)}
                    disabled={studyBusy}
                    style={{ border: '1px solid #dbeafe', background: '#fff', color: '#1d4ed8', borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 600, cursor: studyBusy ? 'not-allowed' : 'pointer' }}
                  >
                    Düzenle
                  </button>)}
                  {canWriteStudy && (<button
                    type="button"
                    onClick={() => openDeleteStudyModal(entry)}
                    disabled={studyBusy}
                    style={{ border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 600, cursor: studyBusy ? 'not-allowed' : 'pointer' }}
                  >
                    Sil
                  </button>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {studyEntries.length > studyLimit && <button className="student360-action" onClick={() => setStudyLimit((value) => value + 5)}>Daha fazla çalışma göster</button>}
  </Card>;
  const booksContent = <>
    {pendingError && <div className="student360-error">Onay bekleyen kitaplar alınamadı.</div>}
      {/* Completed books */}
      <div style={{ marginTop: 12, background: 'linear-gradient(120deg, #F8FAFC 0%, #EEF2FF 100%)', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 12px' }}>
        <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap' }}>Okunan Kitaplar</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto', paddingBottom: 2 }}>
            {canWriteBooks && (<button
              type="button"
              onClick={openAddModal}
              disabled={booksBusy || !studentId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                flex: '0 0 auto',
                border: '1px solid #c7d2fe',
                background: booksBusy ? '#eef2ff' : '#ffffff',
                color: '#3730a3',
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 700,
                cursor: booksBusy || !studentId ? 'not-allowed' : 'pointer',
              }}
            >
              Bitirdiğim kitabı ekle
            </button>)}
            <button
              type="button"
              onClick={() => setBooksScope('30')}
              style={{ display: 'inline-flex', alignItems: 'center', flex: '0 0 auto', background: 'transparent', border: 'none', padding: 0 }}
            >
              <Chip label={`Son 30 gün (${completedBooksLast30.length})`} active={booksScope === '30'} />
            </button>
            <button
              type="button"
              onClick={() => setBooksScope('all')}
              style={{ display: 'inline-flex', alignItems: 'center', flex: '0 0 auto', background: 'transparent', border: 'none', padding: 0 }}
            >
              <Chip label={`Tümü (${completedBooksAll.length})`} active={booksScope === 'all'} />
            </button>
          </div>
        </div>
        {bookNotice && <div role="status" style={{ marginBottom: 12, color: '#334155', fontSize: 14 }}>{bookNotice}</div>}
        {pendingBooks.length > 0 && <section aria-label="Onay bekleyenler" style={{ marginBottom: 16, padding: 12, border: '1px solid #e2e8f0', borderRadius: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Onay bekleyenler ({pendingBooks.length})</div>
          {pendingBooks.map((book) => <div key={book.id} style={{ padding: '6px 0', color: '#475569' }}>
            {book.title}{book.page_count != null ? ` · ${book.page_count} sayfa` : ''}
          </div>)}
        </section>}
        {completedBooks.length === 0 && (
          <div style={{ padding: '10px 12px', background: '#fff', borderRadius: 10, border: '1px dashed #e5e7eb', color: '#475569', fontSize: 14 }}>
            {booksScope === '30' ? 'Son 30 günde tamamlanmış kitap kaydı yok.' : 'Tamamlanmış kitap kaydı yok.'}
          </div>
        )}
        {completedBooks.length > 0 && (
          <div style={{ display: 'grid', gap: 10 }}>
            {completedBooks.slice(0, bookLimit).map((book, idx) => (
              <div key={`${book.title}-${book.date}-${idx}`} style={{ background: '#fff', borderRadius: 10, padding: '10px 12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.03)', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ minWidth: 0, flex: '1 1 180px' }}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{book.title}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Bitiş Tarihi: {formatDate(book.date)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ background: '#EEF2FF', color: '#4338CA', padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                    {formatPageCount(book)} sayfa
                  </div>
                  {canWriteBooks && (<button
                    type="button"
                    onClick={() => openEditModal(book)}
                    disabled={booksBusy || !(Number(book?.completion_id) > 0)}
                    style={{ border: '1px solid #dbeafe', background: '#fff', color: '#1d4ed8', borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 600, cursor: booksBusy ? 'not-allowed' : 'pointer' }}
                  >
                    Düzenle
                  </button>)}
                  {canWriteBooks && (<button
                    type="button"
                    onClick={() => openDeleteModal(book)}
                    disabled={booksBusy || !(Number(book?.completion_id) > 0)}
                    style={{ border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 600, cursor: booksBusy ? 'not-allowed' : 'pointer' }}
                  >
                    Sil
                  </button>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {completedBooks.length > bookLimit && <button className="student360-action" onClick={() => setBookLimit((value) => value + 5)}>Daha fazla kitap göster</button>}
  </>;
  const modals = <>
      {studyModalMode && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'grid', placeItems: 'center', zIndex: 61, padding: 16 }}>
          <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="Çalışma kaydı" style={{ width: '100%', maxWidth: 420, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', boxShadow: '0 10px 30px rgba(15,23,42,0.2)', padding: 16, boxSizing: 'border-box' }}>
            <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>
              {studyModalMode === 'add' ? 'Çalışma Kaydı Ekle' : (studyModalMode === 'edit' ? 'Çalışma Kaydı Düzenle' : 'Çalışma Kaydını Sil')}
            </div>

            {(studyModalMode === 'add' || studyModalMode === 'edit') && (
              <div style={{ display: 'grid', gap: 10 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Tarih</span>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
                    <DatePicker
                      value={toPickerDate(studyForm.date)}
                      onChange={(v) => setStudyForm((prev) => ({ ...prev, date: toIsoDate(v) }))}
                      format="dd.MM.yyyy"
                      disabled={studyBusy}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              fontSize: 14,
                              minHeight: 42,
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                  {ENTRY_SUBJECTS.map(([key, label]) => (
                    <label key={key} style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                      <span style={{ fontSize: 12, color: '#475569' }}>{label} soru</span>
                      <input
                        type="number"
                        min={0}
                        max={10000}
                        value={studyForm.subjects?.[key] || ''}
                        onChange={(e) => setStudyForm((prev) => ({ ...prev, subjects: { ...prev.subjects, [key]: e.target.value } }))}
                        disabled={studyBusy}
                        style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
                      />
                    </label>
                  ))}
                  <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                    <span style={{ fontSize: 12, color: '#475569' }}>Sayfa</span>
                    <input
                      type="number"
                      min={0}
                      value={studyForm.pages}
                      onChange={(e) => setStudyForm((prev) => ({ ...prev, pages: e.target.value }))}
                      disabled={studyBusy}
                      style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
                    />
                  </label>
                </div>
              </div>
            )}

            {studyModalMode === 'delete' && (
              <div style={{ fontSize: 14, color: '#334155' }}>
                <b>{formatDate(studyTarget?.date)}</b> tarihli çalışma kaydı silinecek. Emin misiniz?
              </div>
            )}

            {studyModalError ? (
              <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>
                {studyModalError}
              </div>
            ) : null}

            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={closeStudyModal}
                disabled={studyBusy}
                style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#334155', borderRadius: 8, padding: '8px 12px', fontWeight: 600, cursor: studyBusy ? 'not-allowed' : 'pointer' }}
              >
                İptal
              </button>
              <button
                type="button"
                onClick={submitStudyModal}
                disabled={studyBusy}
                style={{ border: '1px solid #0c4a6e', background: '#0c4a6e', color: '#fff', borderRadius: 8, padding: '8px 12px', fontWeight: 700, cursor: studyBusy ? 'not-allowed' : 'pointer' }}
              >
                {studyModalMode === 'delete' ? 'Sil' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {bookModalMode && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'grid', placeItems: 'center', zIndex: 60, padding: 16 }}>
          <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="Kitap kaydı" style={{ width: '100%', maxWidth: 420, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', boxShadow: '0 10px 30px rgba(15,23,42,0.2)', padding: 16, boxSizing: 'border-box' }}>
            <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>
              {bookModalMode === 'add' ? 'Bitirdiğim kitabı ekle' : (bookModalMode === 'edit' ? 'Kitap Düzenle' : 'Kitap Kaydını Sil')}
            </div>

            {bookModalMode === 'add' && (
              <div style={{ display: 'grid', gap: 10 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Kitap adı</span>
                  <input
                    value={bookForm.title}
                    onChange={(e) => setBookForm((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={booksBusy || bookAddStep === 2}
                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
                  />
                </label>

                {bookAddStep === 1 && bookSearchResult?.similarMatches?.length > 0 && (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
                      Benzer kitaplar bulundu. Uygun olanı seçebilir veya yeni kitap olarak devam edebilirsiniz.
                    </div>
                    {bookSearchResult.similarMatches.map((candidate) => (
                      <button
                        key={`candidate-${candidate.id}`}
                        type="button"
                        onClick={() => selectSimilarBook(candidate)}
                        disabled={booksBusy}
                        style={{
                          textAlign: 'left',
                          border: '1px solid #dbeafe',
                          background: '#f8fbff',
                          color: '#1e3a8a',
                          borderRadius: 8,
                          padding: '8px 10px',
                          cursor: booksBusy ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {candidate.title} {candidate.page_count != null ? `(${candidate.page_count} sayfa)` : ''}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={continueAsNewBook}
                      disabled={booksBusy}
                      style={{
                        border: '1px dashed #cbd5e1',
                        background: '#fff',
                        color: '#334155',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontWeight: 600,
                        cursor: booksBusy ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Bunlar değil, yeni kitap olarak devam et
                    </button>
                  </div>
                )}

                {bookAddStep === 2 && (
                  <>
                    {bookSearchResult?.exactMatch ? (
                      <div style={{ fontSize: 12, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 10px' }}>
                        Aynı isimde kitap bulundu. Sayfa sayısı otomatik dolduruldu, gerekirse güncelleyebilirsiniz.
                      </div>
                    ) : null}
                    <label style={{ display: 'grid', gap: 4 }}>
                      <span style={{ fontSize: 12, color: '#475569' }}>Sayfa sayısı</span>
                      <input
                        type="number"
                        min={1}
                        value={bookForm.pageCount}
                        onChange={(e) => setBookForm((prev) => ({ ...prev, pageCount: e.target.value }))}
                        disabled={booksBusy}
                        style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
                      />
                    </label>
                  </>
                )}
              </div>
            )}

            {bookModalMode === 'edit' && (
              <div style={{ display: 'grid', gap: 10 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Kitap adı</span>
                  <input
                    value={bookForm.title}
                    onChange={(e) => setBookForm((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={booksBusy}
                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Sayfa sayısı</span>
                  <input
                    type="number"
                    min={1}
                    value={bookForm.pageCount}
                    onChange={(e) => setBookForm((prev) => ({ ...prev, pageCount: e.target.value }))}
                    disabled={booksBusy}
                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
                  />
                </label>
              </div>
            )}

            {bookModalMode === 'delete' && (
              <div style={{ fontSize: 14, color: '#334155' }}>
                <b>{bookTarget?.title || 'Bu kayıt'}</b> silinecek. Emin misiniz?
              </div>
            )}

            {bookModalError ? (
              <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>
                {bookModalError}
              </div>
            ) : null}

            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={closeBookModal}
                disabled={booksBusy}
                style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#334155', borderRadius: 8, padding: '8px 12px', fontWeight: 600, cursor: booksBusy ? 'not-allowed' : 'pointer' }}
              >
                İptal
              </button>
              <button
                type="button"
                onClick={bookModalMode === 'add' && bookAddStep === 1 ? searchForAddBook : submitBookModal}
                disabled={booksBusy}
                style={{ border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', borderRadius: 8, padding: '8px 12px', fontWeight: 700, cursor: booksBusy ? 'not-allowed' : 'pointer' }}
              >
                {bookModalMode === 'delete' ? 'Sil' : (bookModalMode === 'add' && bookAddStep === 1 ? 'Devam' : 'Kaydet')}
              </button>
            </div>
          </div>
        </div>
      )}
  </>;
  return splitLayout ? <>
    {studyMount && createPortal(studyContent, studyMount)}
    {booksMount && createPortal(booksContent, booksMount)}
    {modals}
  </> : <Card title="Çalışma ve Okuma">{studyContent}{booksContent}{modals}</Card>;
}
